import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';
import { Sparkline } from '@/components/charts/Sparkline';

export interface KpiCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * KPI title
   */
  title: string;

  /**
   * Main metric value
   */
  value: string | number;

  /**
   * Optional unit (e.g., '$', '%', 'ms')
   */
  unit?: string;

  /**
   * Trend direction
   */
  trend?: 'up' | 'down' | 'stable';

  /**
   * Percentage change (e.g., 12.3 for +12.3%)
   */
  trendValue?: number;

  /**
   * Optional sparkline data points
   */
  sparklineData?: number[];

  /**
   * Optional icon
   */
  icon?: LucideIcon;

  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Optional subtitle/description
   */
  subtitle?: string;

  /**
   * Color theme for trend
   */
  trendColorScheme?: 'default' | 'inverted';
}

const sizeClasses = {
  sm: {
    card: 'p-3',
    title: 'text-xs',
    value: 'text-lg',
    trend: 'text-xs',
    sparkline: 40,
  },
  md: {
    card: 'p-4',
    title: 'text-sm',
    value: 'text-2xl',
    trend: 'text-sm',
    sparkline: 50,
  },
  lg: {
    card: 'p-6',
    title: 'text-base',
    value: 'text-3xl',
    trend: 'text-base',
    sparkline: 60,
  },
};

const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return TrendingUp;
    case 'down':
      return TrendingDown;
    case 'stable':
      return Minus;
    default:
      return null;
  }
};

const getTrendColor = (trend?: 'up' | 'down' | 'stable', inverted = false) => {
  if (!trend) return 'text-fg-muted';

  const colors = {
    up: inverted ? 'text-danger' : 'text-success',
    down: inverted ? 'text-success' : 'text-danger',
    stable: 'text-primary',
  };

  return colors[trend];
};

export const KpiCard = React.memo<KpiCardProps>(
  ({
    title,
    value,
    unit,
    trend,
    trendValue,
    sparklineData,
    icon: Icon,
    size = 'md',
    loading = false,
    subtitle,
    trendColorScheme = 'default',
    className,
    ...props
  }) => {
    const sizeConfig = sizeClasses[size];
    const TrendIcon = getTrendIcon(trend);
    const trendColor = getTrendColor(trend, trendColorScheme === 'inverted');

    if (loading) {
      return (
        <Card
          className={cn(
            'bg-bg-elevated',
            'border-border hover:shadow-glow-primary transition-all duration-150',
            sizeConfig.card,
            className
          )}
          {...props}
        >
          <div className="animate-pulse">
            <div className="h-4 w-24 bg-bg-surface rounded mb-3" />
            <div className="h-8 w-32 bg-bg-surface rounded mb-2" />
            <div className="h-3 w-20 bg-bg-surface rounded" />
          </div>
        </Card>
      );
    }

    return (
      <Card
        className={cn(
          'bg-bg-elevated',
          'border-border hover:shadow-glow-primary transition-all duration-150',
          'relative overflow-hidden',
          sizeConfig.card,
          className
        )}
        {...props}
      >
        {/* Background accent gradient */}
        <div className="absolute inset-0 bg-linear-to-br from-primary/5 to-transparent pointer-events-none" />

        <CardHeader className="p-0 pb-2 relative">
          <CardTitle
            className={cn('flex items-center justify-between', sizeConfig.title)}
          >
            <span className="text-fg-subtle uppercase tracking-wider font-medium flex items-center gap-2">
              {Icon && <Icon className="h-4 w-4 text-primary" />}
              {title}
            </span>
            {TrendIcon && trend && (
              <div className={cn('flex items-center gap-1', trendColor)}>
                <TrendIcon className="h-4 w-4" />
                {trendValue !== undefined && (
                  <span className={cn(sizeConfig.trend, 'tabular-nums')}>
                    {trend === 'up' && '+'}
                    {trend === 'down' && '-'}
                    {Math.abs(trendValue).toFixed(1)}%
                  </span>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0 relative">
          <div className="flex items-end justify-between">
            <div>
              <div
                className={cn(
                  'font-light text-fg-secondary tabular-nums',
                  sizeConfig.value
                )}
              >
                {unit && <span className="text-primary">{unit}</span>}
                {value}
              </div>
              {subtitle && <div className="text-xs text-fg-muted mt-1">{subtitle}</div>}
            </div>

            {sparklineData && sparklineData.length > 0 && (
              <div className="ml-4 opacity-60 hover:opacity-100 transition-opacity">
                <Sparkline
                  data={sparklineData}
                  width={80}
                  height={sizeConfig.sparkline}
                  color={
                    trend === 'up' ? 'success' : trend === 'down' ? 'danger' : 'primary'
                  }
                  type="line"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
);

// Compound components for flexibility
export const KpiCardTitle = React.memo<{
  children: React.ReactNode;
  className?: string;
}>(({ children, className }) => (
  <div
    className={cn(
      'text-sm text-fg-subtle font-medium uppercase tracking-wider',
      className
    )}
  >
    {children}
  </div>
));

export const KpiCardValue = React.memo<{
  children: React.ReactNode;
  className?: string;
  unit?: string;
}>(({ children, className, unit }) => (
  <div className={cn('text-2xl font-light text-fg-secondary tabular-nums', className)}>
    {unit && <span className="text-primary">{unit}</span>}
    {children}
  </div>
));

export const KpiCardTrend = React.memo<{
  trend: 'up' | 'down' | 'stable';
  value?: number;
  className?: string;
  inverted?: boolean;
}>(({ trend, value, className, inverted = false }) => {
  const TrendIcon = getTrendIcon(trend);
  const trendColor = getTrendColor(trend, inverted);

  if (!TrendIcon) return null;

  return (
    <div className={cn('flex items-center gap-1 text-sm', trendColor, className)}>
      <TrendIcon className="h-4 w-4" />
      {value !== undefined && (
        <span>
          {trend === 'up' && '+'}
          {trend === 'down' && '-'}
          {Math.abs(value).toFixed(1)}%
        </span>
      )}
    </div>
  );
});
